/*eslint-disable*/
import qs from 'qs';
import { HOST_API } from 'src/config-global';

const defaults = {
  headers: {
    common: {},
  },
};

const buildHeaders = () => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return {
    'Content-Type': 'application/json',
    'X-Timezone': timezone,
  };
};

const handleResponse = async (response, responseType) => {
  let data;

  try {
    if (responseType === 'blob') {
      data = await response.blob();
    } else if (responseType === 'text') {
      data = await response.text();
    } else {
      data = await response.json();
    }
  } catch {
    data = null;
  }

  if (!response.ok) {
    const error = {
      response: {
        status: response.status,
        data,
      },
    };
    throw error;
  }

  return { data, headers: response.headers };
};

// const handleResponse = async (response) => {
//   let data;

//   try {
//     data = await response.json();
//   } catch {
//     data = null;
//   }

//   if (!response.ok) {
//     const error = {
//       response: {
//         status: response.status,
//         data,
//       },
//     };
//     throw error;
//   }

//   return { data };
// };

const REFRESH_URL = '/api/refresh';

// Single-flight access-token refresh: concurrent 401s share one round-trip.
let refreshPromise = null;

export const refreshTokens = async () => {
  if (refreshPromise) return refreshPromise;

  const refresh_token = localStorage.getItem('refresh_token');
  if (!refresh_token) return null;

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${HOST_API}${REFRESH_URL}`, {
        method: 'POST',
        headers: buildHeaders(),
        credentials: 'include',
        body: JSON.stringify({ refresh_token }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      const token = data.access_token;
      localStorage.setItem('access_token', token);
      // F2-D: the backend rotates the refresh token on every use. Persist the new
      // one — otherwise the next refresh replays a consumed token and trips reuse
      // detection, which revokes the whole session.
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }
      defaults.headers.common.Authorization = `Bearer ${token}`;
      return token;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Invoked when a request is rejected and the session can no longer be renewed
// (refresh token expired/revoked). The auth layer registers forceLogout here.
let onSessionExpired = null;
export const setSessionExpiredHandler = (fn) => {
  onSessionExpired = fn;
};

const request = async (url, options = {}, _retry = false) => {
  const { responseType, ...fetchOptions } = options;

  const doFetch = () =>
    fetch(`${HOST_API}${url}`, {
      headers: {
        ...buildHeaders(),
        ...defaults.headers.common,
        ...(fetchOptions.headers || {}),
      },
      credentials: 'include',
      ...fetchOptions,
    });

  let res = await doFetch();

  // Expired access token: refresh once and replay the original request.
  if (res.status === 401 && !_retry && url !== REFRESH_URL) {
    const token = await refreshTokens();
    if (token) {
      res = await doFetch();
    } else if (localStorage.getItem('access_token') || localStorage.getItem('refresh_token')) {
      // Had a session but it can't be renewed — force the user to login instead
      // of letting the 401 bubble up and leave an empty authenticated screen.
      onSessionExpired?.();
    }
  }

  return handleResponse(res, responseType);
};

export const fetcher = async (args) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const query = config?.params
    ? `?${qs.stringify(config.params, { arrayFormat: 'repeat' })}`
    : '';

  const res = await request(`${url}${query}`, {
    method: 'GET',
  });

  return res.data;
};

// Surface the server's humanized message (FastAPI `detail`) instead of a
// generic string, while keeping the response status/detail on the thrown error
// so callers can branch on it. Falls back to the generic message when the
// response has no usable message.
const buildRequestError = (error) => {
  const data = error.response?.data;
  const message =
    data?.detail || data?.message || 'Algo deu errado ao processar a requisição.';
  const err = new Error(message);
  err.detail = data?.detail;
  err.status = error.response?.status;
  return err;
};

export const post = async (url, data) => {
  try {
    const res = await request(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    return res.data;
  } catch (error) {
    if (error.response?.status >= 400) {
      console.error('Erro 400:', error.response.data);
      throw buildRequestError(error);
    }
    throw error;
  }
};

export const put = async (url, data) => {
  try {
    const res = await request(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    return res.data;
  } catch (error) {
    if (error.response?.status >= 400) {
      console.error('Erro 400:', error.response.data);
      throw buildRequestError(error);
    }
    throw error;
  }
};

export const patch = async (url, data) => {
  try {
    const res = await request(url, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });

    return res.data;
  } catch (error) {
    if (error.response?.status >= 400) {
      console.error('Erro 400:', error.response.data);
      throw buildRequestError(error);
    }
    throw error;
  }
};

export const remove = async (url, data) => {
  try {
    const res = await request(url, {
      method: 'DELETE',
      body: data ? JSON.stringify(data) : undefined,
    });

    return res.data;
  } catch (error) {
    if (error.response?.status >= 400) {
      console.error('Erro 400:', error.response.data);
      throw buildRequestError(error);
    }
    throw error;
  }
};

const clientInstance = {
  defaults,

  get: (url, config = {}) =>
    request(url, { ...config, method: 'GET' }),

  post: (url, data, config = {}) =>
    request(url, {
      ...config,
      method: 'POST',
      body: JSON.stringify(data),
    }),

  put: (url, data, config = {}) =>
    request(url, {
      ...config,
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  patch: (url, data, config = {}) =>
    request(url, {
      ...config,
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (url, config = {}) =>
    request(url, {
      ...config,
      method: 'DELETE',
    }),
};

export default clientInstance;

export const endpoints = {
  auth: {
    login: '/api/login',
    refresh: '/api/refresh',
    logout: '/api/logout',
    me: '/api/me',
    meFeatures: '/api/me/features',
  },
  password: {
    tokenSignUp: `/api/token_sign_up`,
    forgetPassword: `/api/forget_password`,
    newPassword: `/api/reset_password`,
  },
  corporation: {
    list: '/api/corporations',
    update: (corporationId) => `/api/corporations/${corporationId}`,
    details: (corporationId) => `/api/corporations/${corporationId}`,
    syncVehicles: (corporationId) => `/api/corporations/${corporationId}/wialon/vehicles/sync`,
    vehicles: (corporationId) => `/api/corporations/${corporationId}/vehicles`,
    syncDevicesGeofence: (corporationId) => `/api/corporations/${corporationId}/wialon/devices/geofence/sync`,
    devices: (corporationId) => `/api/corporations/${corporationId}/devices`,
    updateLocation: (corporationId, locationId) => `/api/corporations/${corporationId}/locations/${locationId}`,
    syncResources: (corporationId) => `/api/corporations/${corporationId}/wialon/resources/sync`,
    resources: (corporationId) => `/api/corporations/${corporationId}/wialon/resources`,
    currentConstructionSites: (corporationId) => `/api/corporations/${corporationId}/constructions/current`,
    resolveNotification: (corporationId, notificationId) => `/api/corporations/${corporationId}/notifications/${notificationId}`,
    locations: {
      list: (corporationId) => `/api/corporations/${corporationId}/locations`,
      details: (corporationId, locationId) => `/api/corporations/${corporationId}/locations/${locationId}`,
      unloadEvents: (corporationId, locationId) => `/api/corporations/${corporationId}/locations/${locationId}/events/unload`,
      geofenceMode: (corporationId, locationId) => `/api/corporations/${corporationId}/locations/${locationId}/geofence-mode`,
      toggleEvent: (corporationId, locationId, eventId) => `/api/corporations/${corporationId}/locations/${locationId}/events/${eventId}/toggle`,
      relocateGeofence: (corporationId, locationId) => `/api/corporations/${corporationId}/locations/${locationId}/relocate-geofence`,
    },
    geofenceSuggestions: (corporationId) => `/api/corporations/${corporationId}/geofence-suggestions`,
    travel: {
      route: (corporationId, travelId) => `/api/corporations/${corporationId}/travels/${travelId}/route`,
      dischargeEvents: (corporationId, travelId) => `/api/corporations/${corporationId}/travels/${travelId}/discharge-events`,
      geofenceSuggestion: (corporationId, travelId) => `/api/corporations/${corporationId}/travels/${travelId}/geofence-suggestion`,
    },
    trips: {
      search: (corporationId) => `/api/corporations/${corporationId}/trips/search`,
      contractCodes: (corporationId) => `/api/corporations/${corporationId}/contract-codes`,
    },
  },
  fleet: {
    dailyDashboard: (corporationId) => `/api/corporations/${corporationId}/fleet/dashboard/daily`,
    activeTravels: (corporationId) => `/api/corporations/${corporationId}/fleet/active/travel`,
    route: (corporationId) => `/api/corporations/${corporationId}/fleet/route`,
  },
  logistics: {
    corporationUsers: (corporationId) => `/api/corporations/${corporationId}/users`,
    drivers: {
      list: (corporationId) => `/api/corporations/${corporationId}/drivers`,
      details: (corporationId, driverId) => `/api/corporations/${corporationId}/drivers/${driverId}`,
      assignments: (corporationId, driverId) =>
        `/api/corporations/${corporationId}/drivers/${driverId}/assignments`,
    },
    vehicles: {
      list: (corporationId) => `/api/corporations/${corporationId}/vehicles`,
      details: (corporationId, vehicleId) => `/api/corporations/${corporationId}/vehicles/${vehicleId}`,
      assignments: (corporationId, vehicleId) =>
        `/api/corporations/${corporationId}/vehicles/${vehicleId}/assignments`,
      syncTypes: (corporationId) => `/api/corporations/${corporationId}/vehicles/wialon/sync-types`,
    },
    assignment: (corporationId, assignmentId) =>
      `/api/corporations/${corporationId}/assignments/${assignmentId}`,
  },
  roadmap: {
    // features são globais/públicas; sugestões derivam a empresa do usuário
    features: '/api/roadmap/features',
    featureDetails: (featureId) => `/api/roadmap/features/${featureId}`,
    featureInterest: (featureId) => `/api/roadmap/features/${featureId}/interest`,
    featureInterests: (featureId) => `/api/roadmap/features/${featureId}/interests`,
    suggestions: '/api/roadmap/suggestions',
    suggestionsAnalyze: '/api/roadmap/suggestions/analyze',
    suggestionsChat: '/api/roadmap/suggestions/chat',
    suggestionTriage: (suggestionId) => `/api/roadmap/suggestions/${suggestionId}/triage`,
    suggestionSimilar: (suggestionId) => `/api/roadmap/suggestions/${suggestionId}/similar`,
    triageSuggestions: '/api/roadmap/triage/suggestions',
  },
  debug: {
    fleet: '/api/debug/fleet',
  },
  device: {
    list: '/api/devices',
    deviceWithConfigs: (deviceId) => `/api/device/${deviceId}`,
    updateDevice: (deviceId) => `/api/device/${deviceId}`,
    createDeviceUserLink: '/api/user_devices',
    deleteDeviceUserLink: (userId, deviceId) => `/api/deviceuserlink/${userId}/${deviceId}`,
    updateDeleteDeviceConfig: (deviceId, configId) => `/api/device_config/${deviceId}/${configId}`,
    createDeviceConfig: '/api/device_config',
    generateManualToken: '/api/generate_manual_mode_token',
    deviceBackup: (backupId) => `/api/backup/${backupId}`,
    deviceBackups: (deviceId) => `/api/backups/${deviceId}`,
    listDbBackups: (deviceUin) => `/api/backups/db/${deviceUin}`,
    downloadLatestDbBackup: (deviceUin) => `/api/backups/db/${deviceUin}/latest`,
    listControllerBackups: (deviceUin) => `/api/controller_backups/${deviceUin}`,
    downloadControllerBackup: (deviceUin, fileName) => `/api/controller_backups/${deviceUin}/${fileName}`,
  },
  shipment: {
    loadTime: '/api/average_load_time',
    list: '/api/concrete_shipments_report',
    volumeTravelsByDay: '/api/volume_and_travels_by_day',
    volumeTravelsByFormula: '/api/volume_and_travels_by_formula',
    count:'/api/shipment_count',
    volume:'/api/total_volume',
    clients:'/api/sum_contract_code',
    summary: '/api/concrete_shipments_summary',
    consumedMaterials:'/api/consumed_materials_by_type',
    details: '/api/concrete_shipment_by_device_uin_and_devid',
    consumedMaterialsByLocation: '/api/consumed_materials_by_location_and_devid',
    generatePublicToken: `/api/generate_public_token`,
    auditLink: '/api/audit/link',
    ganttData: `/api/shipment_gantt_data`,
    complement: (deviceId, shipmentId) => `/api/shipment_complement/${deviceId}/${shipmentId}`,
    parent: (deviceId, shipmentId) => `/api/parent_shipment/${deviceId}/${shipmentId}`,
  },
  inventory: {
    items: '/api/inventory_items',
    locations: '/api/inventory_locations',
    allocations: '/api/inventory_allocations',
    balances: '/api/inventory_balances',
    balanceBreakdown: '/api/inventory_balance_breakdown',
    ledger: '/api/inventory_ledger',
    entries: '/api/inventory_entries',
    seedStatus: '/api/inventory/seed_status',
    seedOpening: '/api/inventory/seed_opening',
    openings: '/api/inventory/openings',
  },
  operationsTimeline: {
    daily: '/api/operations_timeline',
  },
  monitoredLocations: {
    list: '/api/monitored_locations',
    report: (locationId) => `/api/monitored_locations/${locationId}/report`,
    movements: (locationId) => `/api/monitored_locations/${locationId}/movements`,
  },
  manualOperations: {
    list: '/api/manual_operations',
    linkShipment: (mo_id, cs_id) => `/api/manual_operation/concrete_shipment/link/${mo_id}/${cs_id}`,
    unlinkShipment: (mo_id, cs_id) => `/api/manual_operation/concrete_shipment/unlink/${mo_id}`,
    setStatus: (id) => `/api/manual_operation/set_status/${id}`,
    unsetStatus: (id) => `/api/manual_operation/unset_status/${id}`,
    consumedMaterialsByType: '/api/manual_operations_consumed_materials_by_type'
  },
  manualSensorVariation: {
    create: '/api/manual_sensor_variation',
    update: (id) => `/api/manual_sensor_variation/${id}`,
  },
  public: {
    shipment: {
      details: (token) => `/api/public/concrete_shipment/${token}`,
      consumedMaterialsByLocation: (token) => `/api/public/consumed_materials_by_location/${token}`,
      ganttData: (token) => `/api/public/shipment_gantt_data/${token}`
    },
  },
  users: {
    list: '/api/users',
    userInfo: (id) => `/api/user/${id}`,
    updateOwnUser: () => '/api/user',
    createUser: '/api/user',
    sendNewUserMail: '/api/forget_password'
  },
  tracking: {
    reverseGeocode: '/api/reverse-geocode',
  },
  ai: {
    chat: (corporationId) => `/api/ai/corporations/${corporationId}/chat`,
    usage: (corporationId) => `/api/ai/corporations/${corporationId}/usage`,
    history: (corporationId) => `/api/ai/corporations/${corporationId}/history`,
    sessions: (corporationId) => `/api/ai/corporations/${corporationId}/sessions`,
    session: (corporationId, sessionId) =>
      `/api/ai/corporations/${corporationId}/sessions/${sessionId}`,
  },
  appConfig: {
    activeTheme: '/api/app-config/active-theme',
  },
};
